// src/pages/GestionAgregarNuevo.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, functions } from '../firebase';
import { 
    collection, 
    getDocs, 
    doc, 
    setDoc, 
    addDoc, 
    updateDoc, 
    query, 
    where, 
    serverTimestamp, 
    arrayUnion,
    writeBatch
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import PlatformBackButton from '../components/PlatformBackButton';
import { registerMovement } from '../utils/registerMovement';
import { 
    FaUser, 
    FaUserTie, 
    FaUserShield, 
    FaBuilding, 
    FaShieldAlt, 
    FaKey, 
    FaPhone, 
    FaEnvelope, 
    FaHome, 
    FaDollarSign, 
    FaCheckCircle, 
    FaExclamationTriangle, 
    FaCopy, 
    FaPlus, 
    FaRandom, 
    FaCar, 
    FaPaw, 
    FaIdCard, 
    FaSpinner, 
    FaHistory, 
    FaTimes,
    FaInfoCircle
} from 'react-icons/fa';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import './GestionAgregarNuevo.css';

// Endpoints HTTP de respaldo si el callable falla
const PASSWORD_ENDPOINTS = [
    'https://setuserpasswordhttp-gb5hv5xxaq-uc.a.run.app',
    'https://us-central1-gestionpagosfracc.cloudfunctions.net/setUserPasswordHttp',
];

// Helper para generar contraseñas seguras y legibles
const generateSecurePassword = (prefix = 'Fracc') => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const symbols = '!@#$%&*';
    let randomPart = '';
    for (let i = 0; i < 4; i++) {
        randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const symbol = symbols.charAt(Math.floor(Math.random() * symbols.length));
    const year = new Date().getFullYear();
    return `${prefix}${year}${symbol}${randomPart}`;
};

// Helper para normalizar cadenas en búsquedas
const normalizeString = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
};

const GestionAgregarNuevo = () => {
    const { showNotification } = useNotification();
    const { userData, currentUser } = useAuth();

    // Rol / Entidad activa
    const [activeTab, setActiveTab] = useState('residente'); // 'residente' | 'superadmin' | 'platformadmin' | 'fraccionamiento' | 'caseta'

    // Catálogo de fraccionamientos para dropdowns
    const [fraccionamientos, setFraccionamientos] = useState([]);
    const [loadingFraccs, setLoadingFraccs] = useState(true);

    // Estados de carga y feedback
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [copiedFeedback, setCopiedFeedback] = useState(false);

    // Opciones adicionales
    const [crearOtro, setCrearOtro] = useState(false);
    const [enviarBienvenida, setEnviarBienvenida] = useState(true);

    // Mini bitácora de creaciones recientes de la sesión
    const [recientes, setRecientes] = useState([]);

    // --- FORMULARIO RESIDENTE ---
    const [formResidente, setFormResidente] = useState({
        fraccionamientoId: '',
        numero: '',
        nombre: '',
        email: '',
        telefono: '',
        telefono2: '',
        password: generateSecurePassword('Resid'),
        adeudo: '',
        saldoFavor: '',
        perfil: 'propietario',
        mascotas: false,
        numResidentes: '1',
        numVehiculos: '0',
        tag: '',
        tag2: '',
    });

    // --- FORMULARIO ADMINISTRADOR ---
    const [formAdmin, setFormAdmin] = useState({
        fraccionamientoId: '',
        nombre: '',
        email: '',
        telefono: '',
        cargo: 'Administrador Titular',
        password: generateSecurePassword('Admin'),
    });

    // --- FORMULARIO GERENTE (PLATFORM ADMIN) ---
    const [formGerente, setFormGerente] = useState({
        nombre: '',
        email: '',
        telefono: '',
        password: generateSecurePassword('Gerente'),
        notas: '',
    });

    // --- FORMULARIO FRACCIONAMIENTO ---
    const defaultVencimiento = useMemo(() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        return d.toISOString().split('T')[0];
    }, []);

    const [formFracc, setFormFracc] = useState({
        nombreFracc: '',
        suscripcion: 'Basica',
        fechaVencimiento: defaultVencimiento,
        nombreAdmin: '',
        emailAdmin: '',
        passwordAdmin: generateSecurePassword('AdminFracc'),
        direccion: '',
        cuotaBase: '',
    });

    // --- FORMULARIO CASETA ---
    const [formCaseta, setFormCaseta] = useState({
        fraccionamientoId: '',
        nombre: '',
        identificador: 'Principal',
        email: '',
        telefono: '',
        turno: '24/7',
        password: generateSecurePassword('Caseta'),
    });

    // --- VALIDACIONES EN TIEMPO REAL ---
    const [validatingPhone, setValidatingPhone] = useState(false);
    const [phoneValidation, setPhoneValidation] = useState(null); // { exists: boolean, message: string, type: 'warning' | 'error' }

    const [validatingEmail, setValidatingEmail] = useState(false);
    const [emailValidation, setEmailValidation] = useState(null); // { exists: boolean, user: object, message: string, isMultipropiedad: boolean }

    const [validatingCasa, setValidatingCasa] = useState(false);
    const [casaValidation, setCasaValidation] = useState(null); // { exists: boolean, message: string }

    const [validatingFraccNombre, setValidatingFraccNombre] = useState(false);
    const [fraccNombreValidation, setFraccNombreValidation] = useState(null); // { exists: boolean, message: string }

    // 1. Cargar lista de fraccionamientos al montar
    useEffect(() => {
        const fetchFraccionamientos = async () => {
            try {
                setLoadingFraccs(true);
                const snap = await getDocs(collection(db, 'fraccionamientos'));
                const list = [];
                snap.forEach((d) => {
                    const data = d.data() || {};
                    list.push({
                        id: d.id,
                        nombre: data.nombre || data.nombreFracc || 'Sin nombre',
                        estado: data.estado || 'Activo',
                        adminEmail: data.adminEmail || '',
                    });
                });
                list.sort((a, b) => a.nombre.localeCompare(b.nombre));
                setFraccionamientos(list);

                // Auto-seleccionar el primer fraccionamiento si existe
                if (list.length > 0) {
                    setFormResidente((prev) => ({ ...prev, fraccionamientoId: prev.fraccionamientoId || list[0].id }));
                    setFormAdmin((prev) => ({ ...prev, fraccionamientoId: prev.fraccionamientoId || list[0].id }));
                    setFormCaseta((prev) => ({ ...prev, fraccionamientoId: prev.fraccionamientoId || list[0].id }));
                }
            } catch (err) {
                console.error('Error al cargar fraccionamientos:', err);
                showNotification('Error al cargar la lista de fraccionamientos', 'error');
            } finally {
                setLoadingFraccs(false);
            }
        };

        fetchFraccionamientos();
    }, [showNotification]);

    // Obtener el correo y teléfono a validar según la pestaña activa
    const currentEmail = useMemo(() => {
        switch (activeTab) {
            case 'residente': return formResidente.email.trim().toLowerCase();
            case 'superadmin': return formAdmin.email.trim().toLowerCase();
            case 'platformadmin': return formGerente.email.trim().toLowerCase();
            case 'fraccionamiento': return formFracc.emailAdmin.trim().toLowerCase();
            case 'caseta': return formCaseta.email.trim().toLowerCase();
            default: return '';
        }
    }, [activeTab, formResidente.email, formAdmin.email, formGerente.email, formFracc.emailAdmin, formCaseta.email]);

    const currentPhone = useMemo(() => {
        switch (activeTab) {
            case 'residente': return formResidente.telefono.replace(/\D/g, '');
            case 'superadmin': return formAdmin.telefono.replace(/\D/g, '');
            case 'platformadmin': return formGerente.telefono.replace(/\D/g, '');
            case 'caseta': return formCaseta.telefono.replace(/\D/g, '');
            default: return '';
        }
    }, [activeTab, formResidente.telefono, formAdmin.telefono, formGerente.telefono, formCaseta.telefono]);

    // Helper: Encontrar nombre de fraccionamiento por ID
    const getFraccName = useCallback((id) => {
        const found = fraccionamientos.find((f) => f.id === id);
        return found ? found.nombre : 'Fraccionamiento no especificado';
    }, [fraccionamientos]);

    // 2. Validación en tiempo real del Correo Electrónico
    useEffect(() => {
        if (!currentEmail || currentEmail.length < 5 || !currentEmail.includes('@')) {
            setEmailValidation(null);
            setValidatingEmail(false);
            return;
        }

        const timer = setTimeout(async () => {
            setValidatingEmail(true);
            try {
                const qEmail = query(collection(db, 'usuarios'), where('email', '==', currentEmail));
                const snap = await getDocs(qEmail);

                if (!snap.empty) {
                    const existingDoc = snap.docs[0];
                    const existingData = existingDoc.data() || {};
                    const existingRol = (existingData.rol || '').toLowerCase();
                    const existingName = existingData.nombre || existingData.displayName || 'Usuario registrado';
                    const existingFracc = getFraccName(existingData.fraccionamientoId);

                    if (activeTab === 'residente') {
                        setEmailValidation({
                            exists: true,
                            isMultipropiedad: true,
                            type: 'info',
                            user: existingData,
                            message: `Usuario existente detectado: "${existingName}" (Casa ${existingData.numeroCasa || 'S/N'} en ${existingFracc}). Al guardar, se le vinculará este nuevo domicilio como multipropiedad sin alterar su cuenta de acceso ni su contraseña actual.`,
                        });
                    } else if (activeTab === 'superadmin') {
                        setEmailValidation({
                            exists: true,
                            isMultipropiedad: false,
                            type: 'info',
                            user: existingData,
                            message: `Administrador existente: "${existingName}". Se le asignará este fraccionamiento en su lista de fraccionamientos administrados.`,
                        });
                    } else if (activeTab === 'platformadmin') {
                        setEmailValidation({
                            exists: true,
                            isMultipropiedad: false,
                            type: 'warning',
                            user: existingData,
                            message: `El usuario "${existingName}" ya existe con rol "${existingData.rol || 'registrado'}". Se actualizará a Gerente de Plataforma.`,
                        });
                    } else {
                        setEmailValidation({
                            exists: true,
                            isMultipropiedad: false,
                            type: 'warning',
                            user: existingData,
                            message: `El correo ya está en uso por "${existingName}" (Rol: ${existingRol}).`,
                        });
                    }
                } else {
                    setEmailValidation({
                        exists: false,
                        type: 'success',
                        message: 'Correo disponible para nuevo registro.',
                    });
                }
            } catch (err) {
                console.error('Error validando correo:', err);
            } finally {
                setValidatingEmail(false);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [currentEmail, activeTab, getFraccName]);

    // 3. Validación en tiempo real del Teléfono
    useEffect(() => {
        if (!currentPhone || currentPhone.length < 8) {
            setPhoneValidation(null);
            setValidatingPhone(false);
            return;
        }

        const timer = setTimeout(async () => {
            setValidatingPhone(true);
            try {
                const qPhone = query(collection(db, 'usuarios'), where('telefono', '==', currentPhone));
                const snap = await getDocs(qPhone);

                if (!snap.empty) {
                    const existingData = snap.docs[0].data() || {};
                    const fraccName = getFraccName(existingData.fraccionamientoId);
                    setPhoneValidation({
                        exists: true,
                        type: 'warning',
                        message: `Teléfono ya asociado a "${existingData.nombre || 'Usuario'}" (Rol: ${existingData.rol || 'user'}, ${fraccName}). Puedes usarlo si se trata de la misma persona o familiar.`,
                    });
                } else {
                    setPhoneValidation({
                        exists: false,
                        type: 'success',
                        message: 'Teléfono verificado sin colisiones.',
                    });
                }
            } catch (err) {
                console.error('Error validando teléfono:', err);
            } finally {
                setValidatingPhone(false);
            }
        }, 450);

        return () => clearTimeout(timer);
    }, [currentPhone, getFraccName]);

    // 4. Validación de Vivienda Duplicada en el mismo Fraccionamiento (para Residente)
    useEffect(() => {
        if (activeTab !== 'residente') {
            setCasaValidation(null);
            return;
        }

        const numTrimmed = formResidente.numero.trim();
        const fraccId = formResidente.fraccionamientoId;

        if (!numTrimmed || !fraccId) {
            setCasaValidation(null);
            setValidatingCasa(false);
            return;
        }

        const timer = setTimeout(async () => {
            setValidatingCasa(true);
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccId, 'casas');
                const qCasa = query(casasRef, where('numero', '==', numTrimmed));
                const snap = await getDocs(qCasa);

                if (!snap.empty) {
                    const casaData = snap.docs[0].data() || {};
                    setCasaValidation({
                        exists: true,
                        type: 'warning',
                        message: `La vivienda "${numTrimmed}" ya está registrada a nombre de "${casaData.nombre_residente || 'Sin nombre'}". Si continúas, se actualizarán los datos o creará un inquilino/copropietario.`,
                    });
                } else {
                    setCasaValidation({
                        exists: false,
                        type: 'success',
                        message: `Vivienda "${numTrimmed}" disponible en ${getFraccName(fraccId)}.`,
                    });
                }
            } catch (err) {
                console.error('Error validando casa:', err);
            } finally {
                setValidatingCasa(false);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [activeTab, formResidente.numero, formResidente.fraccionamientoId, getFraccName]);

    // 5. Validación de Nombre Duplicado para Fraccionamiento
    useEffect(() => {
        if (activeTab !== 'fraccionamiento') {
            setFraccNombreValidation(null);
            return;
        }

        const nombreCandidate = normalizeString(formFracc.nombreFracc);
        if (!nombreCandidate || nombreCandidate.length < 3) {
            setFraccNombreValidation(null);
            return;
        }

        setValidatingFraccNombre(true);
        const exists = fraccionamientos.some((f) => normalizeString(f.nombre) === nombreCandidate);

        if (exists) {
            setFraccNombreValidation({
                exists: true,
                type: 'error',
                message: `Ya existe un fraccionamiento registrado con el nombre "${formFracc.nombreFracc.trim()}". Por favor elige un nombre único.`,
            });
        } else {
            setFraccNombreValidation({
                exists: false,
                type: 'success',
                message: 'Nombre disponible para nuevo fraccionamiento.',
            });
        }
        setValidatingFraccNombre(false);
    }, [activeTab, formFracc.nombreFracc, fraccionamientos]);

    // 6. Validación de Incongruencia Financiera (Adeudo vs Saldo a Favor)
    const financialError = useMemo(() => {
        if (activeTab !== 'residente') return null;
        const adeudoNum = parseFloat(formResidente.adeudo) || 0;
        const saldoNum = parseFloat(formResidente.saldoFavor) || 0;

        if (adeudoNum > 0 && saldoNum > 0) {
            return 'Incongruencia financiera detectada: una cuenta no puede tener adeudo pendiente ($' + adeudoNum.toFixed(2) + ') y saldo a favor ($' + saldoNum.toFixed(2) + ') simultáneamente. Por favor deja uno en $0.00.';
        }
        return null;
    }, [activeTab, formResidente.adeudo, formResidente.saldoFavor]);

    // Regenerar contraseña segura para el rol activo
    const handleRegeneratePassword = () => {
        const prefixMap = {
            residente: 'Resid',
            superadmin: 'Admin',
            platformadmin: 'Gerente',
            fraccionamiento: 'AdminFracc',
            caseta: 'Caseta',
        };
        const newPass = generateSecurePassword(prefixMap[activeTab] || 'Domus');

        switch (activeTab) {
            case 'residente':
                setFormResidente((prev) => ({ ...prev, password: newPass }));
                break;
            case 'superadmin':
                setFormAdmin((prev) => ({ ...prev, password: newPass }));
                break;
            case 'platformadmin':
                setFormGerente((prev) => ({ ...prev, password: newPass }));
                break;
            case 'fraccionamiento':
                setFormFracc((prev) => ({ ...prev, passwordAdmin: newPass }));
                break;
            case 'caseta':
                setFormCaseta((prev) => ({ ...prev, password: newPass }));
                break;
            default:
                break;
        }
        showNotification('Nueva contraseña segura generada', 'info');
    };

    // Copiar credenciales al portapapeles
    const handleCopyCredentials = () => {
        let textToCopy = '';
        const currentPass = 
            activeTab === 'residente' ? formResidente.password :
            activeTab === 'superadmin' ? formAdmin.password :
            activeTab === 'platformadmin' ? formGerente.password :
            activeTab === 'fraccionamiento' ? formFracc.passwordAdmin :
            formCaseta.password;

        const roleLabel = 
            activeTab === 'residente' ? 'Residente' :
            activeTab === 'superadmin' ? 'Administrador' :
            activeTab === 'platformadmin' ? 'Gerente de Plataforma' :
            activeTab === 'fraccionamiento' ? 'Administrador de Fraccionamiento' :
            'Caseta de Vigilancia';

        textToCopy = `*CREDENCIALES DE ACCESO - DOMUS HABITA*\n` +
            `Rol: ${roleLabel}\n` +
            `Correo: ${currentEmail || 'Sin correo'}\n` +
            `Contraseña: ${currentPass}\n` +
            `Plataforma: https://mi-fraccionamiento.web.app`;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(textToCopy);
            setCopiedFeedback(true);
            setTimeout(() => setCopiedFeedback(false), 2500);
            showNotification('¡Credenciales copiadas al portapapeles!', 'success');
        } else {
            showNotification('No se pudo copiar automáticamente, copia manualmente.', 'warning');
        }
    };

    // Helper centralizado para crear / asignar contraseña con Firebase Callable o HTTP
    const assignAuthPassword = async (email, password, rol, nombre, telefono) => {
        try {
            const setUserPassFn = httpsCallable(functions, 'setUserPassword');
            const res = await setUserPassFn({
                email,
                password,
                rol,
                nombre,
                telefono,
            });
            return res.data?.uid;
        } catch (callErr) {
            console.warn('setUserPassword callable falló, intentando endpoints HTTP...', callErr);
            let lastErr = null;
            for (const endpoint of PASSWORD_ENDPOINTS) {
                try {
                    const resp = await fetch(endpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, password, rol, nombre, telefono }),
                    });
                    const data = await resp.json();
                    if (resp.ok && data.success && data.uid) {
                        return data.uid;
                    }
                    lastErr = new Error(data.message || 'Error en endpoint de contraseña');
                } catch (netErr) {
                    lastErr = netErr;
                }
            }
            throw lastErr || new Error('No se pudo asignar contraseña al usuario');
        }
    };

    // --- SUBMIT HANDLERS ---
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (financialError) {
            showNotification(financialError, 'error');
            return;
        }

        if (fraccNombreValidation?.exists && activeTab === 'fraccionamiento') {
            showNotification('El nombre del fraccionamiento ya existe en la base de datos.', 'error');
            return;
        }

        setSubmitting(true);

        try {
            if (activeTab === 'residente') {
                await handleCrearResidente();
            } else if (activeTab === 'superadmin') {
                await handleCrearAdministrador();
            } else if (activeTab === 'platformadmin') {
                await handleCrearGerente();
            } else if (activeTab === 'fraccionamiento') {
                await handleCrearFraccionamiento();
            } else if (activeTab === 'caseta') {
                await handleCrearCaseta();
            }
        } catch (err) {
            console.error('Error al procesar registro:', err);
            showNotification(err.message || 'Ocurrió un error inesperado al guardar el registro.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // 1. Guardar Residente
    const handleCrearResidente = async () => {
        const { fraccionamientoId, numero, nombre, email, telefono, telefono2, password, adeudo, saldoFavor, perfil, mascotas, numResidentes, numVehiculos, tag, tag2 } = formResidente;

        if (!fraccionamientoId) throw new Error('Por favor selecciona un fraccionamiento.');
        if (!numero.trim()) throw new Error('Ingresa el número o lote de la vivienda.');
        if (!nombre.trim()) throw new Error('Ingresa el nombre completo del residente.');
        if (!email.trim() || !email.includes('@')) throw new Error('Ingresa un correo electrónico válido.');
        if (!telefono.trim()) throw new Error('Ingresa el número telefónico del residente.');
        if (!password || password.length < 6) throw new Error('La contraseña debe tener al menos 6 caracteres.');

        const normalizedEmail = email.trim().toLowerCase();
        const adeudoNum = parseFloat(adeudo) || 0;
        const saldoFavorNum = parseFloat(saldoFavor) || 0;

        // 1. Crear o actualizar Auth
        const uid = await assignAuthPassword(normalizedEmail, password, 'user', nombre, telefono);
        if (!uid) throw new Error('No se pudo generar el identificador de usuario en Authentication.');

        // 2. Batch de Firestore
        const batch = writeBatch(db);

        // a) Documento en colección usuarios
        const userRef = doc(db, 'usuarios', uid);
        batch.set(userRef, {
            uid,
            nombre: nombre.trim(),
            displayName: nombre.trim(),
            email: normalizedEmail,
            telefono: telefono.trim(),
            telefono2: telefono2 ? telefono2.trim() : '',
            numeroCasa: numero.trim(),
            rol: 'user',
            fraccionamientoId,
            fraccionamientos: arrayUnion(fraccionamientoId),
            estado: 'activo',
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
        }, { merge: true });

        // b) Documento en subcolección fraccionamientos/{id}/casas
        const casasCol = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const nuevaCasaRef = doc(casasCol);

        const tagsPayload = {};
        if (tag.trim()) tagsPayload.tag = tag.trim();
        if (tag2.trim()) tagsPayload.tag2 = tag2.trim();

        batch.set(nuevaCasaRef, {
            numero: numero.trim(),
            nombre_residente: nombre.trim(),
            email: normalizedEmail,
            telefono: telefono.trim(),
            telefono2: telefono2 ? telefono2.trim() : '',
            perfil: perfil || 'propietario',
            disponible: true,
            moroso: adeudoNum > 0,
            adeudo: adeudoNum,
            saldoFavor: saldoFavorNum,
            adeudoCapital: adeudoNum,
            adeudoIntereses: 0,
            mascotas: Boolean(mascotas),
            num_residentes: parseInt(numResidentes, 10) || 1,
            num_vehiculos: parseInt(numVehiculos, 10) || 0,
            userId: uid,
            ...tagsPayload,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });

        await batch.commit();

        // 3. Registrar movimiento en la bitácora del fraccionamiento
        try {
            await registerMovement({
                fraccionamientoId,
                titulo: `Nuevo residente registrado: ${nombre.trim()} (${numero.trim()})`,
                descripcion: `Vivienda ${numero.trim()} - Email: ${normalizedEmail}`,
                categoria: 'Catálogo',
                tipoMovimiento: 'residente_creado',
                origen: 'agregar_nuevo',
                userData,
                currentUser,
            });
        } catch (logErr) {
            console.warn('No se pudo registrar movimiento:', logErr);
        }

        // Agregar a bitácora local
        const nuevoRegistro = {
            id: nuevaCasaRef.id,
            rol: 'Residente',
            nombre: nombre.trim(),
            email: normalizedEmail,
            detalle: `Casa ${numero.trim()}`,
            fraccNombre: getFraccName(fraccionamientoId),
            fecha: new Date(),
        };
        setRecientes((prev) => [nuevoRegistro, ...prev]);

        showNotification(`¡Residente ${nombre.trim()} registrado exitosamente en ${getFraccName(fraccionamientoId)}!`, 'success');

        if (crearOtro) {
            setFormResidente((prev) => ({
                ...prev,
                numero: '',
                nombre: '',
                email: '',
                telefono: '',
                telefono2: '',
                password: generateSecurePassword('Resid'),
                adeudo: '',
                saldoFavor: '',
                tag: '',
                tag2: '',
            }));
        } else {
            setFormResidente({
                fraccionamientoId,
                numero: '',
                nombre: '',
                email: '',
                telefono: '',
                telefono2: '',
                password: generateSecurePassword('Resid'),
                adeudo: '',
                saldoFavor: '',
                perfil: 'propietario',
                mascotas: false,
                numResidentes: '1',
                numVehiculos: '0',
                tag: '',
                tag2: '',
            });
        }
    };

    // 2. Guardar Administrador
    const handleCrearAdministrador = async () => {
        const { fraccionamientoId, nombre, email, telefono, cargo, password } = formAdmin;

        if (!fraccionamientoId) throw new Error('Por favor selecciona un fraccionamiento.');
        if (!nombre.trim()) throw new Error('Ingresa el nombre del administrador.');
        if (!email.trim() || !email.includes('@')) throw new Error('Ingresa un correo electrónico válido.');
        if (!telefono.trim()) throw new Error('Ingresa el teléfono del administrador.');
        if (!password || password.length < 6) throw new Error('La contraseña debe tener al menos 6 caracteres.');

        const normalizedEmail = email.trim().toLowerCase();

        // 1. Crear o actualizar Auth
        const uid = await assignAuthPassword(normalizedEmail, password, 'superadmin', nombre, telefono);
        if (!uid) throw new Error('No se pudo generar el usuario administrador en Authentication.');

        // 2. Batch de Firestore
        const batch = writeBatch(db);

        // a) Documento usuarios
        const userRef = doc(db, 'usuarios', uid);
        batch.set(userRef, {
            uid,
            nombre: nombre.trim(),
            displayName: nombre.trim(),
            email: normalizedEmail,
            telefono: telefono.trim(),
            cargo: cargo.trim() || 'Administrador Titular',
            rol: 'superadmin',
            fraccionamientoId,
            fraccionamientosAsignados: arrayUnion(fraccionamientoId),
            estado: 'activo',
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
        }, { merge: true });

        // b) Vincular como admin en el fraccionamiento
        const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
        batch.update(fraccRef, {
            adminId: uid,
            adminNombre: nombre.trim(),
            adminEmail: normalizedEmail,
            updatedAt: serverTimestamp(),
        });

        await batch.commit();

        try {
            await registerMovement({
                fraccionamientoId,
                titulo: `Nuevo Administrador asignado: ${nombre.trim()}`,
                descripcion: `Email: ${normalizedEmail} - Cargo: ${cargo.trim()}`,
                categoria: 'Administración',
                tipoMovimiento: 'admin_creado',
                origen: 'agregar_nuevo',
                userData,
                currentUser,
            });
        } catch (logErr) {
            console.warn('No se pudo registrar movimiento:', logErr);
        }

        const nuevoRegistro = {
            id: uid,
            rol: 'Administrador',
            nombre: nombre.trim(),
            email: normalizedEmail,
            detalle: cargo.trim() || 'Admin Titular',
            fraccNombre: getFraccName(fraccionamientoId),
            fecha: new Date(),
        };
        setRecientes((prev) => [nuevoRegistro, ...prev]);

        showNotification(`¡Administrador ${nombre.trim()} asignado con éxito a ${getFraccName(fraccionamientoId)}!`, 'success');

        if (crearOtro) {
            setFormAdmin((prev) => ({
                ...prev,
                nombre: '',
                email: '',
                telefono: '',
                password: generateSecurePassword('Admin'),
            }));
        } else {
            setFormAdmin({
                fraccionamientoId,
                nombre: '',
                email: '',
                telefono: '',
                cargo: 'Administrador Titular',
                password: generateSecurePassword('Admin'),
            });
        }
    };

    // 3. Guardar Gerente de Plataforma
    const handleCrearGerente = async () => {
        const { nombre, email, telefono, password, notas } = formGerente;

        if (!nombre.trim()) throw new Error('Ingresa el nombre del gerente.');
        if (!email.trim() || !email.includes('@')) throw new Error('Ingresa un correo electrónico válido.');
        if (!telefono.trim()) throw new Error('Ingresa el teléfono del gerente.');
        if (!password || password.length < 6) throw new Error('La contraseña debe tener al menos 6 caracteres.');

        const normalizedEmail = email.trim().toLowerCase();

        // 1. Crear en Auth con rol platformadmin
        const uid = await assignAuthPassword(normalizedEmail, password, 'platformadmin', nombre, telefono);
        if (!uid) throw new Error('No se pudo generar el usuario gerente en Authentication.');

        // 2. Guardar en Firestore usuarios
        const userRef = doc(db, 'usuarios', uid);
        await setDoc(userRef, {
            uid,
            nombre: nombre.trim(),
            displayName: nombre.trim(),
            email: normalizedEmail,
            telefono: telefono.trim(),
            notas: notas.trim() || '',
            rol: 'platformadmin',
            estado: 'activo',
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
        }, { merge: true });

        const nuevoRegistro = {
            id: uid,
            rol: 'Gerente',
            nombre: nombre.trim(),
            email: normalizedEmail,
            detalle: 'Acceso Global Plataforma',
            fraccNombre: 'Todos los Desarrollos',
            fecha: new Date(),
        };
        setRecientes((prev) => [nuevoRegistro, ...prev]);

        showNotification(`¡Gerente de Plataforma ${nombre.trim()} creado exitosamente!`, 'success');

        setFormGerente({
            nombre: '',
            email: '',
            telefono: '',
            password: generateSecurePassword('Gerente'),
            notas: '',
        });
    };

    // 4. Guardar Fraccionamiento (vía Cloud Function crearNuevoFraccionamiento)
    const handleCrearFraccionamiento = async () => {
        const { nombreFracc, suscripcion, fechaVencimiento, nombreAdmin, emailAdmin, passwordAdmin } = formFracc;

        if (!nombreFracc.trim()) throw new Error('Ingresa el nombre del fraccionamiento.');
        if (!nombreAdmin.trim()) throw new Error('Ingresa el nombre del administrador principal.');
        if (!emailAdmin.trim() || !emailAdmin.includes('@')) throw new Error('Ingresa el correo del administrador.');
        if (!passwordAdmin || passwordAdmin.length < 6) throw new Error('La contraseña del administrador debe tener al menos 6 caracteres.');
        if (!fechaVencimiento) throw new Error('Indica la fecha de vencimiento de la suscripción.');

        const crearFn = httpsCallable(functions, 'crearNuevoFraccionamiento');
        const res = await crearFn({
            nombreFracc: nombreFracc.trim(),
            suscripcion,
            fechaVencimiento,
            nombreAdmin: nombreAdmin.trim(),
            emailAdmin: emailAdmin.trim().toLowerCase(),
            passwordAdmin,
        });

        const nuevoRegistro = {
            id: res.data?.fraccionamientoId || Date.now(),
            rol: 'Fraccionamiento',
            nombre: nombreFracc.trim(),
            email: emailAdmin.trim().toLowerCase(),
            detalle: `Plan: ${suscripcion}`,
            fraccNombre: nombreFracc.trim(),
            fecha: new Date(),
        };
        setRecientes((prev) => [nuevoRegistro, ...prev]);

        showNotification(res.data?.message || `¡Fraccionamiento "${nombreFracc.trim()}" registrado con éxito!`, 'success');

        // Actualizar lista local de fraccionamientos
        setFraccionamientos((prev) => [
            ...prev,
            { id: res.data?.fraccionamientoId || String(Date.now()), nombre: nombreFracc.trim(), estado: 'Activo' },
        ]);

        setFormFracc({
            nombreFracc: '',
            suscripcion: 'Basica',
            fechaVencimiento: defaultVencimiento,
            nombreAdmin: '',
            emailAdmin: '',
            passwordAdmin: generateSecurePassword('AdminFracc'),
            direccion: '',
            cuotaBase: '',
        });
    };

    // 5. Guardar Caseta
    const handleCrearCaseta = async () => {
        const { fraccionamientoId, nombre, identificador, email, telefono, turno, password } = formCaseta;

        if (!fraccionamientoId) throw new Error('Por favor selecciona un fraccionamiento.');
        if (!nombre.trim()) throw new Error('Ingresa el nombre o puesto de la caseta.');
        if (!email.trim() || !email.includes('@')) throw new Error('Ingresa el correo de acceso para caseta.');
        if (!password || password.length < 6) throw new Error('La contraseña debe tener al menos 6 caracteres.');

        const normalizedEmail = email.trim().toLowerCase();

        // 1. Crear en Auth con rol caseta
        const uid = await assignAuthPassword(normalizedEmail, password, 'caseta', nombre, telefono);
        if (!uid) throw new Error('No se pudo generar el usuario de caseta en Authentication.');

        // 2. Batch de Firestore
        const batch = writeBatch(db);

        // a) Documento usuarios
        const userRef = doc(db, 'usuarios', uid);
        batch.set(userRef, {
            uid,
            nombre: nombre.trim(),
            displayName: nombre.trim(),
            email: normalizedEmail,
            telefono: telefono.trim() || '',
            rol: 'caseta',
            fraccionamientoId,
            fraccionamientos: [fraccionamientoId],
            turno: turno.trim() || '24/7',
            identificador: identificador.trim() || 'Principal',
            estado: 'activo',
            createdByImport: true, // compatibilidad con panel de vigilancia
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
        }, { merge: true });

        // b) Documento en casas para caseta (sin deudas contables)
        const casasCol = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const casetaCasaRef = doc(casasCol);
        batch.set(casetaCasaRef, {
            numero: `Caseta ${identificador.trim() || 'Principal'}`,
            nombre_residente: nombre.trim(),
            email: normalizedEmail,
            telefono: telefono.trim() || '',
            perfil: 'caseta',
            disponible: false,
            moroso: false,
            adeudo: 0,
            saldoFavor: 0,
            adeudoCapital: 0,
            adeudoIntereses: 0,
            userId: uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });

        await batch.commit();

        try {
            await registerMovement({
                fraccionamientoId,
                titulo: `Nueva Caseta registrada: ${nombre.trim()}`,
                descripcion: `Ubicación: ${identificador.trim()} - Email: ${normalizedEmail}`,
                categoria: 'Vigilancia',
                tipoMovimiento: 'caseta_creada',
                origen: 'agregar_nuevo',
                userData,
                currentUser,
            });
        } catch (logErr) {
            console.warn('No se pudo registrar movimiento:', logErr);
        }

        const nuevoRegistro = {
            id: uid,
            rol: 'Caseta',
            nombre: nombre.trim(),
            email: normalizedEmail,
            detalle: `Turno: ${turno.trim()}`,
            fraccNombre: getFraccName(fraccionamientoId),
            fecha: new Date(),
        };
        setRecientes((prev) => [nuevoRegistro, ...prev]);

        showNotification(`¡Caseta "${nombre.trim()}" creada con éxito en ${getFraccName(fraccionamientoId)}!`, 'success');

        if (crearOtro) {
            setFormCaseta((prev) => ({
                ...prev,
                nombre: '',
                email: '',
                telefono: '',
                password: generateSecurePassword('Caseta'),
            }));
        } else {
            setFormCaseta({
                fraccionamientoId,
                nombre: '',
                identificador: 'Principal',
                email: '',
                telefono: '',
                turno: '24/7',
                password: generateSecurePassword('Caseta'),
            });
        }
    };

    return (
        <div className="gestion-agregar-nuevo-container">
            {/* Header Zen */}
            <div className="section-header-zen">
                <div className="header-left-zen">
                    <PlatformBackButton to="/configuracion-plataforma" />
                    <div className="header-icon-circle purple-glow">
                        <FaPlus />
                    </div>
                    <div>
                        <h2>Agregar Nuevo Registro</h2>
                        <p>Crea residentes, administradores, gerentes, casetas y nuevos fraccionamientos con validación inteligente.</p>
                    </div>
                </div>
            </div>

            {/* Selector de Rol / Pestañas Zen */}
            <div className="role-tabs-bar">
                <button
                    type="button"
                    className={`role-tab-btn ${activeTab === 'residente' ? 'active' : ''}`}
                    onClick={() => setActiveTab('residente')}
                >
                    <FaUser className="tab-icon" />
                    <span>Residente</span>
                    <small className="tab-badge-role">user</small>
                </button>

                <button
                    type="button"
                    className={`role-tab-btn ${activeTab === 'superadmin' ? 'active' : ''}`}
                    onClick={() => setActiveTab('superadmin')}
                >
                    <FaUserTie className="tab-icon" />
                    <span>Administrador</span>
                    <small className="tab-badge-role">superadmin</small>
                </button>

                <button
                    type="button"
                    className={`role-tab-btn ${activeTab === 'platformadmin' ? 'active' : ''}`}
                    onClick={() => setActiveTab('platformadmin')}
                >
                    <FaUserShield className="tab-icon" />
                    <span>Gerente</span>
                    <small className="tab-badge-role">platformadmin</small>
                </button>

                <button
                    type="button"
                    className={`role-tab-btn ${activeTab === 'fraccionamiento' ? 'active' : ''}`}
                    onClick={() => setActiveTab('fraccionamiento')}
                >
                    <FaBuilding className="tab-icon" />
                    <span>Fraccionamiento</span>
                    <small className="tab-badge-role">nuevo</small>
                </button>

                <button
                    type="button"
                    className={`role-tab-btn ${activeTab === 'caseta' ? 'active' : ''}`}
                    onClick={() => setActiveTab('caseta')}
                >
                    <FaShieldAlt className="tab-icon" />
                    <span>Caseta</span>
                    <small className="tab-badge-role">caseta</small>
                </button>
            </div>

            {/* Banner de Validación Inteligente (cuando aplica) */}
            {emailValidation && (
                <div className={`smart-validation-banner banner-${emailValidation.type}`}>
                    {emailValidation.type === 'info' ? <FaInfoCircle /> : emailValidation.type === 'warning' ? <FaExclamationTriangle /> : <FaCheckCircle />}
                    <div className="banner-text">
                        <strong>Detección de Cuenta:</strong> {emailValidation.message}
                    </div>
                </div>
            )}

            {phoneValidation?.exists && (
                <div className="smart-validation-banner banner-warning">
                    <FaPhone />
                    <div className="banner-text">
                        <strong>Teléfono en uso:</strong> {phoneValidation.message}
                    </div>
                </div>
            )}

            {casaValidation?.exists && (
                <div className="smart-validation-banner banner-warning">
                    <FaHome />
                    <div className="banner-text">
                        <strong>Vivienda ya registrada:</strong> {casaValidation.message}
                    </div>
                </div>
            )}

            {fraccNombreValidation?.exists && (
                <div className="smart-validation-banner banner-error">
                    <FaExclamationTriangle />
                    <div className="banner-text">
                        <strong>Nombre Duplicado:</strong> {fraccNombreValidation.message}
                    </div>
                </div>
            )}

            {financialError && (
                <div className="smart-validation-banner banner-error">
                    <FaExclamationTriangle />
                    <div className="banner-text">
                        <strong>Bloqueo Contable:</strong> {financialError}
                    </div>
                </div>
            )}

            {/* Contenedor del Formulario Zen */}
            <form onSubmit={handleSubmit} className="zen-form-card">
                {/* 1. PESTAÑA: RESIDENTE */}
                {activeTab === 'residente' && (
                    <div className="form-fields-grid">
                        <div className="form-group-zen full-width">
                            <label htmlFor="res-fracc">
                                Fraccionamiento Asignado <span className="req">*</span>
                            </label>
                            <select
                                id="res-fracc"
                                value={formResidente.fraccionamientoId}
                                onChange={(e) => setFormResidente({ ...formResidente, fraccionamientoId: e.target.value })}
                                required
                            >
                                <option value="" disabled>Selecciona un fraccionamiento...</option>
                                {fraccionamientos.map((f) => (
                                    <option key={f.id} value={f.id}>{f.nombre}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-numero">
                                Número de Casa / Lote <span className="req">*</span>
                                {validatingCasa && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="text"
                                id="res-numero"
                                placeholder="Ej: 104, Manzana 3 Lote 8, Depto 2B"
                                value={formResidente.numero}
                                onChange={(e) => setFormResidente({ ...formResidente, numero: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-nombre">
                                Nombre Completo del Residente <span className="req">*</span>
                            </label>
                            <input
                                type="text"
                                id="res-nombre"
                                placeholder="Ej: Carlos Meneses Chávez"
                                value={formResidente.nombre}
                                onChange={(e) => setFormResidente({ ...formResidente, nombre: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-email">
                                Correo Electrónico (Usuario de acceso) <span className="req">*</span>
                                {validatingEmail && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="email"
                                id="res-email"
                                placeholder="ejemplo@correo.com"
                                value={formResidente.email}
                                onChange={(e) => setFormResidente({ ...formResidente, email: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-telefono">
                                Teléfono Móvil (10 dígitos) <span className="req">*</span>
                                {validatingPhone && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="tel"
                                id="res-telefono"
                                placeholder="Ej: 4421234567"
                                value={formResidente.telefono}
                                onChange={(e) => setFormResidente({ ...formResidente, telefono: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-telefono2">
                                Teléfono Secundario / Fijo (Opcional)
                            </label>
                            <input
                                type="tel"
                                id="res-telefono2"
                                placeholder="Teléfono de oficina o casa"
                                value={formResidente.telefono2}
                                onChange={(e) => setFormResidente({ ...formResidente, telefono2: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-perfil">Tipo de Residente</label>
                            <select
                                id="res-perfil"
                                value={formResidente.perfil}
                                onChange={(e) => setFormResidente({ ...formResidente, perfil: e.target.value })}
                            >
                                <option value="propietario">Propietario</option>
                                <option value="inquilino">Inquilino / Arrendatario</option>
                                <option value="habitante">Familiar / Habitante</option>
                            </select>
                        </div>

                        {/* Contraseña con Generador y Copiar */}
                        <div className="form-group-zen full-width password-zen-block">
                            <label htmlFor="res-password">
                                Contraseña Inicial de Acceso <span className="req">*</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="res-password"
                                    value={formResidente.password}
                                    onChange={(e) => setFormResidente({ ...formResidente, password: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn-pass-action"
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? 'Ocultar' : 'Ver'}
                                >
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-regen"
                                    onClick={handleRegeneratePassword}
                                    title="Generar nueva contraseña segura"
                                >
                                    <FaRandom /> Generar
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-copy"
                                    onClick={handleCopyCredentials}
                                    title="Copiar credenciales"
                                >
                                    <FaCopy /> {copiedFeedback ? '¡Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>

                        {/* Bloque Financiero Zen (Blindaje de Saldos) */}
                        <div className="financial-zen-section full-width">
                            <div className="financial-section-title">
                                <FaDollarSign />
                                <div>
                                    <strong>Saldos Iniciales Contables (Categoría A)</strong>
                                    <small>Asigna adeudo inicial de mantenimiento O saldo a favor. No ambos.</small>
                                </div>
                            </div>
                            <div className="financial-inputs-row">
                                <div className="financial-input-box">
                                    <label htmlFor="res-adeudo">Adeudo Inicial ($ MXN)</label>
                                    <input
                                        type="number"
                                        id="res-adeudo"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        value={formResidente.adeudo}
                                        onChange={(e) => setFormResidente({ ...formResidente, adeudo: e.target.value })}
                                    />
                                    <small className="help-text">Mora o saldo pendiente al ingresar</small>
                                </div>

                                <div className="financial-input-box">
                                    <label htmlFor="res-saldofavor">Saldo a Favor Inicial ($ MXN)</label>
                                    <input
                                        type="number"
                                        id="res-saldofavor"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        value={formResidente.saldoFavor}
                                        onChange={(e) => setFormResidente({ ...formResidente, saldoFavor: e.target.value })}
                                    />
                                    <small className="help-text">Anticipo o saldo acreedor del residente</small>
                                </div>
                            </div>
                        </div>

                        {/* Datos Adicionales Compactos */}
                        <div className="form-group-zen">
                            <label htmlFor="res-tag">Tag RFID Principal (Acceso vehicular)</label>
                            <input
                                type="text"
                                id="res-tag"
                                placeholder="Ej: E2004105240201"
                                value={formResidente.tag}
                                onChange={(e) => setFormResidente({ ...formResidente, tag: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-tag2">Tag RFID Secundario (Opcional)</label>
                            <input
                                type="text"
                                id="res-tag2"
                                placeholder="Ej: E2004105240202"
                                value={formResidente.tag2}
                                onChange={(e) => setFormResidente({ ...formResidente, tag2: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-residentes">Número de Habitantes</label>
                            <input
                                type="number"
                                id="res-residentes"
                                min="1"
                                value={formResidente.numResidentes}
                                onChange={(e) => setFormResidente({ ...formResidente, numResidentes: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="res-vehiculos">Número de Vehículos</label>
                            <input
                                type="number"
                                id="res-vehiculos"
                                min="0"
                                value={formResidente.numVehiculos}
                                onChange={(e) => setFormResidente({ ...formResidente, numVehiculos: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen checkbox-zen-group full-width">
                            <label className="checkbox-zen-label">
                                <input
                                    type="checkbox"
                                    checked={formResidente.mascotas}
                                    onChange={(e) => setFormResidente({ ...formResidente, mascotas: e.target.checked })}
                                />
                                <span><FaPaw /> Cuenta con mascotas en la propiedad</span>
                            </label>
                        </div>
                    </div>
                )}

                {/* 2. PESTAÑA: ADMINISTRADOR */}
                {activeTab === 'superadmin' && (
                    <div className="form-fields-grid">
                        <div className="form-group-zen full-width">
                            <label htmlFor="adm-fracc">
                                Fraccionamiento a Administrar <span className="req">*</span>
                            </label>
                            <select
                                id="adm-fracc"
                                value={formAdmin.fraccionamientoId}
                                onChange={(e) => setFormAdmin({ ...formAdmin, fraccionamientoId: e.target.value })}
                                required
                            >
                                <option value="" disabled>Selecciona el fraccionamiento...</option>
                                {fraccionamientos.map((f) => (
                                    <option key={f.id} value={f.id}>{f.nombre}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="adm-nombre">
                                Nombre del Administrador <span className="req">*</span>
                            </label>
                            <input
                                type="text"
                                id="adm-nombre"
                                placeholder="Ej: Lic. Roberto García"
                                value={formAdmin.nombre}
                                onChange={(e) => setFormAdmin({ ...formAdmin, nombre: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="adm-email">
                                Correo Electrónico (Acceso Admin) <span className="req">*</span>
                                {validatingEmail && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="email"
                                id="adm-email"
                                placeholder="admin@fraccionamiento.com"
                                value={formAdmin.email}
                                onChange={(e) => setFormAdmin({ ...formAdmin, email: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="adm-telefono">
                                Teléfono Móvil <span className="req">*</span>
                                {validatingPhone && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="tel"
                                id="adm-telefono"
                                placeholder="Ej: 4429876543"
                                value={formAdmin.telefono}
                                onChange={(e) => setFormAdmin({ ...formAdmin, telefono: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="adm-cargo">Cargo / Rol Específico</label>
                            <input
                                type="text"
                                id="adm-cargo"
                                placeholder="Ej: Administrador Titular, Mesa Directiva, Tesorero"
                                value={formAdmin.cargo}
                                onChange={(e) => setFormAdmin({ ...formAdmin, cargo: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen full-width password-zen-block">
                            <label htmlFor="adm-password">
                                Contraseña de Acceso <span className="req">*</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="adm-password"
                                    value={formAdmin.password}
                                    onChange={(e) => setFormAdmin({ ...formAdmin, password: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn-pass-action"
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? 'Ocultar' : 'Ver'}
                                >
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-regen"
                                    onClick={handleRegeneratePassword}
                                    title="Generar nueva contraseña segura"
                                >
                                    <FaRandom /> Generar
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-copy"
                                    onClick={handleCopyCredentials}
                                    title="Copiar credenciales"
                                >
                                    <FaCopy /> {copiedFeedback ? '¡Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 3. PESTAÑA: GERENTE (PLATFORMADMIN) */}
                {activeTab === 'platformadmin' && (
                    <div className="form-fields-grid">
                        <div className="form-group-zen">
                            <label htmlFor="ger-nombre">
                                Nombre Completo del Gerente <span className="req">*</span>
                            </label>
                            <input
                                type="text"
                                id="ger-nombre"
                                placeholder="Ej: Ing. Jonnatan Wong"
                                value={formGerente.nombre}
                                onChange={(e) => setFormGerente({ ...formGerente, nombre: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="ger-email">
                                Correo Electrónico Institucional <span className="req">*</span>
                                {validatingEmail && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="email"
                                id="ger-email"
                                placeholder="gerencia@plataforma.com"
                                value={formGerente.email}
                                onChange={(e) => setFormGerente({ ...formGerente, email: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="ger-telefono">
                                Teléfono de Contacto <span className="req">*</span>
                                {validatingPhone && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="tel"
                                id="ger-telefono"
                                placeholder="Ej: 5512345678"
                                value={formGerente.telefono}
                                onChange={(e) => setFormGerente({ ...formGerente, telefono: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="ger-notas">Notas o Autorización</label>
                            <input
                                type="text"
                                id="ger-notas"
                                placeholder="Ej: Gerente Operativo Regional"
                                value={formGerente.notas}
                                onChange={(e) => setFormGerente({ ...formGerente, notas: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen full-width password-zen-block">
                            <label htmlFor="ger-password">
                                Contraseña de Alta Seguridad <span className="req">*</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="ger-password"
                                    value={formGerente.password}
                                    onChange={(e) => setFormGerente({ ...formGerente, password: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn-pass-action"
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? 'Ocultar' : 'Ver'}
                                >
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-regen"
                                    onClick={handleRegeneratePassword}
                                    title="Generar nueva contraseña segura"
                                >
                                    <FaRandom /> Generar
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-copy"
                                    onClick={handleCopyCredentials}
                                    title="Copiar credenciales"
                                >
                                    <FaCopy /> {copiedFeedback ? '¡Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. PESTAÑA: FRACCIONAMIENTO */}
                {activeTab === 'fraccionamiento' && (
                    <div className="form-fields-grid">
                        <div className="form-group-zen full-width">
                            <label htmlFor="fracc-nombre">
                                Nombre del Fraccionamiento / Condominio <span className="req">*</span>
                                {validatingFraccNombre && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="text"
                                id="fracc-nombre"
                                placeholder="Ej: Residencial Las Cumbres"
                                value={formFracc.nombreFracc}
                                onChange={(e) => setFormFracc({ ...formFracc, nombreFracc: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="fracc-suscripcion">Tipo de Suscripción</label>
                            <select
                                id="fracc-suscripcion"
                                value={formFracc.suscripcion}
                                onChange={(e) => setFormFracc({ ...formFracc, suscripcion: e.target.value })}
                            >
                                <option value="Basica">Básica (Hasta 50 lotes)</option>
                                <option value="Pro">Profesional (Hasta 200 lotes)</option>
                                <option value="Empresarial">Empresarial (Ilimitado)</option>
                            </select>
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="fracc-vencimiento">
                                Fecha de Vencimiento de Suscripción <span className="req">*</span>
                            </label>
                            <input
                                type="date"
                                id="fracc-vencimiento"
                                value={formFracc.fechaVencimiento}
                                onChange={(e) => setFormFracc({ ...formFracc, fechaVencimiento: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="fracc-admin-nom">
                                Nombre del Administrador Inicial <span className="req">*</span>
                            </label>
                            <input
                                type="text"
                                id="fracc-admin-nom"
                                placeholder="Ej: Fernando Solís"
                                value={formFracc.nombreAdmin}
                                onChange={(e) => setFormFracc({ ...formFracc, nombreAdmin: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="fracc-admin-email">
                                Correo del Administrador Inicial <span className="req">*</span>
                            </label>
                            <input
                                type="email"
                                id="fracc-admin-email"
                                placeholder="admin@cumbres.com"
                                value={formFracc.emailAdmin}
                                onChange={(e) => setFormFracc({ ...formFracc, emailAdmin: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen full-width password-zen-block">
                            <label htmlFor="fracc-admin-pass">
                                Contraseña del Administrador Inicial <span className="req">*</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="fracc-admin-pass"
                                    value={formFracc.passwordAdmin}
                                    onChange={(e) => setFormFracc({ ...formFracc, passwordAdmin: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn-pass-action"
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? 'Ocultar' : 'Ver'}
                                >
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-regen"
                                    onClick={handleRegeneratePassword}
                                    title="Generar nueva contraseña segura"
                                >
                                    <FaRandom /> Generar
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-copy"
                                    onClick={handleCopyCredentials}
                                    title="Copiar credenciales"
                                >
                                    <FaCopy /> {copiedFeedback ? '¡Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 5. PESTAÑA: CASETA */}
                {activeTab === 'caseta' && (
                    <div className="form-fields-grid">
                        <div className="form-group-zen full-width">
                            <label htmlFor="cas-fracc">
                                Fraccionamiento Asignado <span className="req">*</span>
                            </label>
                            <select
                                id="cas-fracc"
                                value={formCaseta.fraccionamientoId}
                                onChange={(e) => setFormCaseta({ ...formCaseta, fraccionamientoId: e.target.value })}
                                required
                            >
                                <option value="" disabled>Selecciona el fraccionamiento...</option>
                                {fraccionamientos.map((f) => (
                                    <option key={f.id} value={f.id}>{f.nombre}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="cas-nombre">
                                Nombre de la Caseta / Vigilancia <span className="req">*</span>
                            </label>
                            <input
                                type="text"
                                id="cas-nombre"
                                placeholder="Ej: Vigilancia Acceso Principal"
                                value={formCaseta.nombre}
                                onChange={(e) => setFormCaseta({ ...formCaseta, nombre: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="cas-identificador">Ubicación / Puerta</label>
                            <input
                                type="text"
                                id="cas-identificador"
                                placeholder="Ej: Norte, Principal, Pluma 2"
                                value={formCaseta.identificador}
                                onChange={(e) => setFormCaseta({ ...formCaseta, identificador: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="cas-email">
                                Correo de Acceso a Caseta <span className="req">*</span>
                                {validatingEmail && <FaSpinner className="inline-spinner" />}
                            </label>
                            <input
                                type="email"
                                id="cas-email"
                                placeholder="caseta.norte@fraccionamiento.com"
                                value={formCaseta.email}
                                onChange={(e) => setFormCaseta({ ...formCaseta, email: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="cas-telefono">Teléfono / Radio de Caseta</label>
                            <input
                                type="tel"
                                id="cas-telefono"
                                placeholder="Ej: 4425550199"
                                value={formCaseta.telefono}
                                onChange={(e) => setFormCaseta({ ...formCaseta, telefono: e.target.value })}
                            />
                        </div>

                        <div className="form-group-zen">
                            <label htmlFor="cas-turno">Turno Operativo</label>
                            <select
                                id="cas-turno"
                                value={formCaseta.turno}
                                onChange={(e) => setFormCaseta({ ...formCaseta, turno: e.target.value })}
                            >
                                <option value="24/7">24 Horas / 7 Días</option>
                                <option value="Matutino">Turno Matutino (06:00 - 14:00)</option>
                                <option value="Vespertino">Turno Vespertino (14:00 - 22:00)</option>
                                <option value="Nocturno">Turno Nocturno (22:00 - 06:00)</option>
                            </select>
                        </div>

                        <div className="form-group-zen full-width password-zen-block">
                            <label htmlFor="cas-password">
                                Contraseña de Acceso para Guardia <span className="req">*</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="cas-password"
                                    value={formCaseta.password}
                                    onChange={(e) => setFormCaseta({ ...formCaseta, password: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn-pass-action"
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? 'Ocultar' : 'Ver'}
                                >
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-regen"
                                    onClick={handleRegeneratePassword}
                                    title="Generar nueva contraseña segura"
                                >
                                    <FaRandom /> Generar
                                </button>
                                <button
                                    type="button"
                                    className="btn-pass-action btn-copy"
                                    onClick={handleCopyCredentials}
                                    title="Copiar credenciales"
                                >
                                    <FaCopy /> {copiedFeedback ? '¡Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Opciones de Flujo Continuo Zen */}
                <div className="form-flow-options">
                    <label className="checkbox-flow-label">
                        <input
                            type="checkbox"
                            checked={crearOtro}
                            onChange={(e) => setCrearOtro(e.target.checked)}
                        />
                        <span>Crear otro registro del mismo tipo inmediatamente al terminar</span>
                    </label>
                </div>

                {/* Acciones del Formulario */}
                <div className="form-actions-zen">
                    <button
                        type="button"
                        className="btn-cancel-zen"
                        onClick={() => window.history.back()}
                        disabled={submitting}
                    >
                        Cancelar
                    </button>

                    <button
                        type="submit"
                        className="btn-submit-zen"
                        disabled={submitting || Boolean(financialError) || (activeTab === 'fraccionamiento' && fraccNombreValidation?.exists)}
                    >
                        {submitting ? (
                            <>
                                <FaSpinner className="inline-spinner spin" /> Procesando Guardado...
                            </>
                        ) : (
                            <>
                                <FaCheckCircle /> Guardar y Registrar
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Mini Bitácora Zen de Sesión ("Registros Creados Recientemente") */}
            {recientes.length > 0 && (
                <div className="recientes-zen-card">
                    <div className="recientes-header">
                        <div className="recientes-title">
                            <FaHistory />
                            <strong>Registros Creados en esta Sesión ({recientes.length})</strong>
                        </div>
                        <small>Cuentas dadas de alta exitosamente durante tu sesión activa</small>
                    </div>

                    <div className="recientes-list">
                        {recientes.map((rec) => (
                            <div key={rec.id} className="reciente-item-card">
                                <span className={`badge-role-pill role-${normalizeString(rec.rol)}`}>
                                    {rec.rol}
                                </span>
                                <div className="reciente-main-info">
                                    <strong>{rec.nombre}</strong>
                                    <small>{rec.email}</small>
                                </div>
                                <div className="reciente-fracc-info">
                                    <span>{rec.fraccNombre}</span>
                                    <small>{rec.detalle}</small>
                                </div>
                                <div className="reciente-time">
                                    {rec.fecha.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestionAgregarNuevo;
